import React, { ReactNode } from 'react';
import { UiLoader } from './UiLoader';

/**
 * Button component matching AngularJS .button styles EXACTLY
 *
 * Source of truth from main.css:
 *
 * .button {
 *   border: 1px solid #0070cd;
 *   border-radius: 20px;
 *   padding: .5em 2em;
 *   font-size: 0.8em;
 *   font-family: 'mont-light';
 *   text-transform: uppercase;
 *   color: #0070cd;
 *   background: none;
 *   transition: all, 250ms, ease-in;
 *   text-align: center;
 *   cursor: pointer;
 * }
 *
 * .button:hover {
 *   text-decoration: none;
 *   background: #0070cd;
 *   color: #fff;
 * }
 *
 * .button.green {
 *   border-color: #53b50a;
 *   color: #53b50a;
 * }
 *
 * .button.green:hover {
 *   background: #53b50a;
 *   color: #fff;
 * }
 *
 * .button.white {
 *   border-color: #fff;
 *   color: #fff;
 * }
 *
 * .button.white:hover {
 *   background: #fff;
 *   color: #0070cd;
 * }
 *
 * .button.grey {
 *   border-color: #acacac;
 *   color: #acacac;
 * }
 *
 * .button[disabled] {
 *   border-color: #acacac;
 *   color: #acacac;
 * }
 *
 * .button[disabled]:hover {
 *   background: #555;
 *   color: #acacac;
 *   cursor: not-allowed;
 * }
 *
 * NO CHANGES to logic, behavior, or structure - only visual parity.
 */

export interface UiButtonProps {
  children: ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  /**
   * Matches Angular .button variants (pill): primary (blue), green, white, grey
   * Also matches Bootstrap 3 .btn.* variants (rectangular) for legacy-parity parity ports:
   * bootstrapGrey = .btn-default, bootstrapInfo = .btn-info, bootstrapWarning = .btn-warning
   */
  variant?:
    | 'primary'
    | 'green'
    | 'white'
    | 'grey'
    | 'bootstrapGrey'
    | 'bootstrapInfo'
    | 'bootstrapWarning';
  className?: string;
  href?: string;
  style?: React.CSSProperties;
}

export const UiButton = ({
  children,
  onClick,
  type = 'button',
  disabled = false,
  loading = false,
  variant = 'primary',
  className = '',
  href,
  style,
}: UiButtonProps) => {
  const isDisabled = disabled || loading;

  const isBootstrap =
    variant === 'bootstrapGrey' || variant === 'bootstrapInfo' || variant === 'bootstrapWarning';

  // Base classes matching .button from main.css (or Bootstrap 3 .btn shape for 'bootstrap*' variants)
  const baseClasses = isBootstrap
    ? [
        'border',
        'border-solid',
        'rounded-[4px]', // Bootstrap border-radius: 4px
        'py-[6px]', // Bootstrap padding: 6px 12px
        'px-[12px]',
        'text-[14px]', // Bootstrap font-size: 14px
        'leading-[1.42857143]', // Bootstrap line-height
        'text-center',
        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
        'inline-block',
        'align-middle',
        'select-none',
        'whitespace-nowrap',
        'no-underline',
        'transition-all',
        'duration-[250ms]',
        'ease-in',
      ].join(' ')
    : [
        'border', // border: 1px solid
        'border-solid',
        'rounded-angular-pill', // border-radius: 20px
        'py-[0.5em]', // padding: .5em 2em (vertical)
        'px-[2em]', // padding: .5em 2em (horizontal)
        'text-angular-button', // font-size: 0.8em
        'font-mont-light', // font-family: 'mont-light'
        'uppercase', // text-transform: uppercase
        'bg-transparent', // background: none
        'transition-all', // transition: all, 250ms, ease-in
        'duration-[250ms]',
        'ease-in',
        'text-center', // text-align: center
        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer', // cursor: pointer or not-allowed
        'inline-block',
        'align-middle',
        'select-none',
        'whitespace-nowrap',
        'no-underline', // text-decoration: none (for links)
      ].join(' ');

  // Variant-specific classes matching Angular button colors EXACTLY
  const variantClasses = {
    // .button default (blue)
    // Disabled state adds opacity-60 to make the grey border/text more
    // visually distinct from the enabled state. AngularJS only changed the
    // color to #acacac with no opacity, which was too subtle — users reported
    // the disabled button looking the same as the enabled button.
    primary: isDisabled
      ? 'border-angular-disabled text-angular-disabled opacity-60 hover:bg-transparent hover:text-angular-disabled cursor-not-allowed'
      : 'border-angular-blue text-angular-blue hover:bg-angular-blue hover:text-white hover:no-underline',

    // .button.green
    green: isDisabled
      ? 'border-angular-disabled text-angular-disabled opacity-60 hover:bg-transparent hover:text-angular-disabled cursor-not-allowed'
      : 'border-angular-green text-angular-green hover:bg-angular-green hover:text-white hover:no-underline',

    // .button.white
    white: isDisabled
      ? 'border-angular-disabled text-angular-disabled opacity-60 hover:bg-transparent hover:text-angular-disabled cursor-not-allowed'
      : 'border-white text-white hover:bg-white hover:text-angular-blue hover:no-underline',

    // .button.grey
    grey: isDisabled
      ? 'border-angular-disabled text-angular-disabled opacity-60 hover:bg-transparent hover:text-angular-disabled cursor-not-allowed'
      : 'border-angular-disabled text-angular-disabled hover:bg-transparent hover:text-angular-disabled',

    // Bootstrap .btn.btn-default (white bg, grey border, dark text, rectangular)
    bootstrapGrey: isDisabled
      ? 'border-[#ccc] bg-white text-[#333] opacity-65 cursor-not-allowed'
      : 'border-[#ccc] bg-white text-[#333] hover:bg-[#e6e6e6] hover:border-[#adadad]',

    // Bootstrap .btn.btn-info (cyan bg, white text, rectangular)
    //   color:#fff; bg:#5bc0de; border:#46b8da; hover bg:#31b0d5 border:#269abc
    bootstrapInfo: isDisabled
      ? 'border-[#46b8da] bg-[#5bc0de] text-white opacity-65 cursor-not-allowed'
      : 'border-[#46b8da] bg-[#5bc0de] text-white hover:bg-[#31b0d5] hover:border-[#269abc] hover:text-white hover:no-underline',

    // Bootstrap .btn.btn-warning (orange bg, white text, rectangular)
    //   color:#fff; bg:#f0ad4e; border:#eea236; hover bg:#ec971f border:#d58512
    bootstrapWarning: isDisabled
      ? 'border-[#eea236] bg-[#f0ad4e] text-white opacity-65 cursor-not-allowed'
      : 'border-[#eea236] bg-[#f0ad4e] text-white hover:bg-[#ec971f] hover:border-[#d58512] hover:text-white hover:no-underline',
  };

  const allClasses = `${baseClasses} ${variantClasses[variant]} ${className}`;

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
        className='button blue'
        style={style}
        aria-disabled={isDisabled}
      >
        {loading && <UiLoader size='sm' className='mr-2' />}
        {children}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={allClasses}
      style={style}
    >
      {loading && <UiLoader size='sm' className='mr-2' />}
      {children}
    </button>
  );
};
